import type { Node as PMNode, Schema } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";
import type { Editor } from "@tiptap/react";

/**
 * Formatting groups for repeated rich-text fields (e.g. every step title, every
 * card text). Changing one field's block tag from the toolbar applies the same
 * tag to every other field in its group, so a group always shares one tag.
 *
 * Editors register by a group key (`${blockId}:${role}`) on mount and unregister
 * on unmount. Kept outside React state (a plain module Map) so registration
 * never triggers re-renders.
 */
const groups = new Map<string, Set<Editor>>();

export function registerGroupEditor(key: string, editor: Editor) {
    let set = groups.get(key);
    if (!set) {
        set = new Set();
        groups.set(key, set);
    }
    set.add(editor);
}

export function unregisterGroupEditor(key: string, editor: Editor) {
    const set = groups.get(key);
    if (!set) return;
    set.delete(editor);
    if (set.size === 0) groups.delete(key);
}

/** The block tags the toolbar can set (paragraph variants + headings). */
export type BlockTag =
    | "paragraph"
    | "label"
    | "small"
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6";

/** A toolbar tag as a node type + attrs (label/small are paragraph flags). */
export const tagMarkup = (schema: Schema, tag: BlockTag) =>
    tag.startsWith("h")
        ? {
              type: schema.nodes.heading,
              attrs: { level: Number(tag.slice(1)) },
          }
        : {
              type: schema.nodes.paragraph,
              attrs: { label: tag === "label", small: tag === "small" },
          };

/** The toolbar tag of a textblock. */
export function nodeTag(node: PMNode | null | undefined): BlockTag | null {
    if (!node?.isTextblock) return null;
    if (node.type.name === "heading") return `h${node.attrs.level}` as BlockTag;
    if (node.attrs.label) return "label";
    if (node.attrs.small) return "small";
    return "paragraph";
}

/**
 * The group's current tag, read from another field (one with text first).
 * Null when the field has no siblings.
 */
export function groupTag(key: string | null, except: object): BlockTag | null {
    if (!key) return null;
    const others = Array.from(groups.get(key) ?? []).filter(
        (e) => e !== except && !e.isDestroyed
    );
    const source = others.find((e) => !e.isEmpty) ?? others[0];
    return source ? nodeTag(source.state.doc.firstChild) : null;
}

/** Marks the author's own tag change (toolbar dropdown, group mirror), which
 *  `KeepHeadingOnEmpty` must never undo — even on an empty heading. */
export const TAG_CHANGE_META = "blockTagChange";

/** Marks a tag change that only shows the group's tag in an empty field. */
export const GROUP_SEED_META = "groupSeed";

/**
 * Show the group's tag in its empty fields — a new step title starts as the
 * group's H1, not the registry H5. Runs on every registration, so mount order
 * doesn't matter. Not saved and not undoable; typing saves it like any edit.
 */
export function seedEmptyFromGroup(key: string, newcomer: Editor) {
    const editors = Array.from(groups.get(key) ?? []).filter(
        (e) => !e.isDestroyed
    );
    const source =
        editors.find((e) => !e.isEmpty) ?? editors.find((e) => e !== newcomer);
    const tag = nodeTag(source?.state.doc.firstChild);
    if (!source || !tag) return;
    for (const editor of editors) {
        const first = editor.state.doc.firstChild;
        if (editor === source || !editor.isEmpty || !first?.isTextblock)
            continue;
        const { type, attrs } = tagMarkup(editor.schema, tag);
        const next = type.create({ ...first.attrs, ...attrs }).attrs;
        if (first.hasMarkup(type, next)) continue;
        editor.view.dispatch(
            editor.state.tr
                .setNodeMarkup(0, type, next)
                .setMeta("preventUpdate", true)
                .setMeta("addToHistory", false)
                .setMeta(GROUP_SEED_META, true)
        );
    }
}

/**
 * Mirror a change onto every other field in the group — but only when it was
 * made on the changed field's FIRST line, and only onto the siblings' first
 * line. A group shares its title line; lines 2+ are local to each field (D8).
 *
 * Only BLOCK-level properties belong here (tag, alignment, direction). Marks
 * (bold / italic / underline / colour) deliberately do NOT sync: they apply to
 * a selected RANGE, so "bold this word" has no meaningful equivalent in a
 * sibling field. Group-level emphasis belongs in a block's Appearance settings.
 */
function forEachSiblingFirstLine(
    key: string | null,
    except: Editor,
    fn: (tr: Transaction, first: PMNode) => void
) {
    if (!key || except.state.selection.$from.index(0) !== 0) return;
    groups.get(key)?.forEach((editor) => {
        const first = editor.state.doc.firstChild;
        if (editor === except || editor.isDestroyed || !first?.isTextblock)
            return;
        const tr = editor.state.tr.setMeta(TAG_CHANGE_META, true);
        fn(tr, first);
        if (tr.docChanged) editor.view.dispatch(tr);
    });
}

/** Apply a tag to the first line of every other field in the group. */
export function applyBlockTagToGroup(
    key: string | null,
    tag: BlockTag,
    except: Editor
) {
    forEachSiblingFirstLine(key, except, (tr, first) => {
        const { type, attrs } = tagMarkup(tr.doc.type.schema, tag);
        tr.setNodeMarkup(0, type, { ...first.attrs, ...attrs });
    });
}

/** Apply a text alignment to the first line of every other field. */
export function applyAlignToGroup(
    key: string | null,
    align: "left" | "center" | "right",
    except: Editor
) {
    forEachSiblingFirstLine(key, except, (tr) =>
        tr.setNodeAttribute(0, "textAlign", align)
    );
}

/** Apply a text direction to the first line of every other field. */
export function applyDirToGroup(
    key: string | null,
    dir: "ltr" | "rtl",
    except: Editor
) {
    forEachSiblingFirstLine(key, except, (tr) =>
        tr.setNodeAttribute(0, "dir", dir)
    );
}
